import { ModelMapFilter } from "modules/modelMap/models/ModelMapFilter";
import { ModelMapFormat } from "modules/modelMap/models/ModelMapFormat";
import { ModelMapFormatErrors } from "modules/modelMap/models/ModelMapFormatErrors";
import {
  TDocumentSnapshot,
  TFirestoreTimestamp,
  TQueryDocumentSnapshot,
} from "common/types/FirebaseTypes";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import { now } from "utils/FirebaseUtils";
import { IUsers } from "common/interfaces";
import { EModelMapType } from "common/enums";

/** Tipo de erros em modelos nos model-maps.
 * @memberof ModelMap
 */
export type TModelError = { label: string; id: string };

/**
 * Modelo de model-map.
 * @category ModelMaps
 * @interface ModelMap
 * @hideconstructor
 */
export class ModelMap {
  name: string;
  developmentId: string;
  files: string[];
  useAllModels: boolean;
  type: EModelMapType;
  types: Array<string>;
  createdAt: TFirestoreTimestamp;
  modifiedAt?: TFirestoreTimestamp;
  fields: Array<string>;
  filters?: Array<ModelMapFilter>;
  formats?: Array<ModelMapFormat>;
  formatErrors?: Array<ModelMapFormatErrors>;
  filterErrors?: Array<string>;
  modelErrors?: Array<TModelError>;
  categoryErrors?: Array<string>;
  users: object;
  warning?: boolean;
  status?: string;
  costPlans?: string[];
  costPlansNeedtoBeUpdated?: string[];
  // PRUBK-423 - Utilizacao de array tipado de numeros para setar o estilo de varios elementos simultaneamente
  GUID?: any;
  constructor(
    name: string,
    developmentId: string,
    files: string[],
    useAllModels: boolean,
    type: EModelMapType,
    types: Array<string>,
    createdAt: TFirestoreTimestamp,
    fields: Array<string>,
    users: object,
    filters?: Array<ModelMapFilter>,
    formats?: Array<ModelMapFormat>,
    formatErrors?: Array<ModelMapFormatErrors>,
    filterErrors?: Array<string>,
    modelErrors?: Array<TModelError>,
    categoryErrors?: Array<string>,
    modifiedAt?: TFirestoreTimestamp,
    warning?: boolean,
    status?: string,
    GUID?: any,
    costPlans?: string[],
    costPlansNeedtoBeUpdated?: string[]
  ) {
    /** Nome do model-map.
     * @type {string}
     */
    this.name = name;
    /** ID do projeto.
     * @type {string}
     */
    this.developmentId = developmentId;
    /** Lista de IDs dos arquivos selecionados quando não for marcado useAllModels.
     * @type {Array<string>}
     */
    this.files = files;
    /** Flag que define se o model-map deve utilizar todos os modelos do projeto ou apenas os selecionados (salvos na propriedades files).
     * @type {boolean}
     */
    this.useAllModels = useAllModels == undefined ? true : useAllModels;
    /** Tipo do model-map. Os valores possíveis são:</br>
     * EC - Elementos construtivos</br>
     * MAT - Materiais
     * @type {string}
     */
    this.type = type;
    /** Lista dos ObjectTypes dos ECs ou lista de materiais quando o type for 'MAT'.
     * @type {Array<string>}
     */
    this.types = types;
    /** Data de criação do model-map.
     * @type {TFirestoreTimestamp}
     */
    this.createdAt = createdAt;
    /** Data de modificação do model-map.
     * @type {TFirestoreTimestamp}
     */
    this.modifiedAt = modifiedAt;
    /** As propriedades das categorias selecionadas para o model-map.
     * @type {Array<string>}
     */
    this.fields = fields;
    /** ?
     * @type {object}
     */
    this.users = users;
    /** Filtros aplicados no model-map.
     * @type {Array<ModelMapFilter>=}
     */
    this.filters = filters;
    /** Formatação aplicada no model-map.
     * @type {Array<ModelMapFormat>=}
     */
    this.formats = formats;
    /** Erros na formatalçao do model-map.
     * @type {Array<ModelMapFormatErrors>=}
     */
    this.formatErrors = formatErrors;
    /** Erros no filtro do model-map.
     * @type {Array<string>=}
     */
    this.filterErrors = filterErrors;
    /** Erros nos modelos selecionados para o model-map.
     * @type {Array<TModelError>=}
     */
    this.modelErrors = modelErrors;
    /** Erros de categorias do model-map.
     * @type {Array<string>=}
     */
    this.categoryErrors = categoryErrors;
    /** Flag que define se houve atualização no model-map para verificar se há necessidade de atualizar as linhas da EAP.
     * @type {boolean=}
     * @depreciated
     */
    this.warning = warning;
    /** Status do model-map.
     * @type {string=}
     */
    this.status = status;
    /** Lista com os GUID dos elementos dos modelos 3D utilizados para extração dos quantitativos.
     * @type {any=}
     */
    if (GUID) this.GUID = GUID;
    /** ID dos planos de custo que possuem vínculo com o model-map.
     * @type {Array<string>=}
     */
    this.costPlans = costPlans;
    /** ID dos planos de custo com vínculo que precisam ser atualizados com a atualização da extração.
     * @type {Array<string>=}
     */
    this.costPlansNeedtoBeUpdated = costPlansNeedtoBeUpdated;
  }
}

export function createNewModeMap(
  name: string,
  devId: string,
  filesIds: string[],
  useAllModels: boolean,
  type: EModelMapType,
  types: string[],
  users: IUsers
) {
  return new ModelMap(
    name,
    devId,
    filesIds,
    useAllModels != undefined ? useAllModels : true,
    type,
    types,
    now(),
    [],
    users,
    [],
    [],
    [],
    [],
    [],
    [],
    now(),
    false,
    "processed",
    [],
    [],
    []
  );
}

export function createModelMapModelFromDocument(
  doc: TQueryDocumentSnapshot | TDocumentSnapshot
): FirebaseDocument<ModelMap> {
  const data = doc.data() as any;
  if (!data) throw new Error("Invalid model-map data!");

  const modelMap = new ModelMap(
    data.name,
    data.developmentId,
    data.files,
    data.useAllModels == undefined ? true : data.useAllModels,
    data.type,
    data.types,
    data.createdAt,
    data.fields,
    data.users,
    data.filters,
    data.formats,
    // Para não ocorrer erro quando ocorre a exclusão do field formatErrors, deve ser atribuído valor default de array ao setar o model map
    data.formatErrors || [],
    data.filterErrors || [],
    data.modelErrors || [],
    data.categoryErrors || [],
    data.modifiedAt,
    data.warning || false,
    data.status || "processed",
    // PRUBK-423 - Utilização de array tipado de números para setar o estilo de vários elementos simultaneamente
    data.GUID || [],
    data.costPlans || [],
    data.costPlansNeedtoBeUpdated || []
  );
  return new FirebaseDocument<ModelMap>(doc.id, modelMap);
}



// WEBPACK FOOTER //
// ./src/modules/modelMap/models/ModelMap.ts